'use client';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Image from 'next/image';
import React from 'react';

import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { getImageUrl } from '@/lib/helpers/getImageUrl';
import {
  ComponentBlocksFaqList,
  ComponentBlocksQuestionList,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types';

import GlobalBlockRenderer from '../GlobalBlockRenderer/GlobalBlockRenderer';

export function FaqListPlainWithGrouping({ props }: { props: ComponentBlocksFaqList }) {
  const theme = useTheme();

  const questionGroups = (props.Questions as ComponentBlocksQuestionList[]).reduce(
    (acc, question) => {
      const key = question.Group || 'Ungrouped';
      if (!acc[key]) acc[key] = [];
      acc[key].push(question);
      return acc;
    },
    {} as Record<string, ComponentBlocksQuestionList[]>
  );

  let accordionCounter = 0;

  const faqContent = (
    <Box sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {Object.entries(questionGroups).map(([groupTitle, questions]) => (
        <Box key={groupTitle} sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            {groupTitle}
          </Typography>
          {questions.map((q, i) => {
            accordionCounter++;
            return (
              <Accordion
                key={i}
                defaultExpanded={accordionCounter === 1}
                sx={(theme) => ({
                  borderBottom: `solid 1px ${theme.palette.divider}`,
                  listStyle: 'none',
                })}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        fontSize: 30,
                        color: theme.palette.common.black,
                      }}
                    />
                  }
                  aria-controls={`panel-${q.id}-content`}
                  id={`panel-${q.id}-header`}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1.3rem' }}>
                    {q.Question.replace(/^\d+\.\s*/, '')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <GlobalBlockRenderer content={q.Answer || []} />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      ))}
    </Box>
  );

  return (
    <CustomBackground
      backgroundOptions={(props?.FaqListBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        {
          ...props?.FaqListDisplayOptions,
          ContentsWidth:
            props?.FaqListDisplayOptions?.ContentsWidth ??
            Enum_Componentshareddisplayoptions_Contentswidth.Wide,
          PaddingTop: props?.FaqListDisplayOptions?.PaddingTop ?? 'md',
          PaddingBottom: props?.FaqListDisplayOptions?.PaddingBottom ?? 'md',
          PaddingInline: props?.FaqListDisplayOptions?.PaddingInline ?? 'xs',
        } as StrapiDisplayOptions
      }
      boxProps={{
        // @ts-expect-error - this does work, but types are not correct
        'data-component': 'FaqListWithGrouping',
      }}
    >
      {/* If image is provided → title on top, then grid */}
      {props.FaqListImage ? (
        <>
          {/* Heading on top */}
          {props.Body && (
            <Box
              sx={{
                '& h2:first-of-type, & p, & h3, & h4': {
                  textAlign: 'center',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                },
              }}
            >
              <GlobalBlockRenderer content={props.Body || []} forceHeadingLevel={2} />
            </Box>
          )}

          <Grid container spacing={6} alignItems="flex-start">
            {/* LEFT COLUMN → Image */}
            <Grid item xs={12} md={5}>
              <Box sx={{ textAlign: 'center' }}>
                <Image
                  src={getImageUrl({ image: props.FaqListImage, size: 'url' })!}
                  alt={props.FaqListImage.alternativeText || 'FAQ illustration'}
                  width={props.FaqListImage.width || 500}
                  height={props.FaqListImage.height || 400}
                  style={{ maxWidth: '100%', height: 'auto', borderRadius: 2 }}
                />
              </Box>
            </Grid>

            {/* RIGHT COLUMN → FAQs */}
            <Grid item xs={12} md={7}>
              {faqContent}
            </Grid>
          </Grid>
        </>
      ) : (
        <Grid container spacing={6} alignItems="flex-start">
          {/* LEFT COLUMN → Body content instead of image */}
          <Grid item xs={12} md={5}>
            {props.Body && (
              <Box
                sx={{
                  my: 4,
                  '& h2:first-of-type, & p, & h3, & h4': {
                    textAlign: { xs: 'center', md: 'left' },
                    marginLeft: { xs: 'auto', md: 0 },
                    marginRight: { xs: 'auto', md: 0 },
                  },
                }}
              >
                <GlobalBlockRenderer content={props.Body || []} forceHeadingLevel={2} />
              </Box>
            )}
          </Grid>

          {/* RIGHT COLUMN → FAQs */}
          <Grid item xs={12} md={7}>
            {faqContent}
          </Grid>
        </Grid>
      )}
    </CustomBackground>
  );
}
